import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const PURCHASE_CONFIRMATION_RETRY_TIMEOUT_MS = 45000;
const PURCHASE_CONFIRMATION_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

function formatTokens(tokens) {
  return Number(tokens || 0).toLocaleString();
}

function getSessionId(search = window.location.search) {
  return new URLSearchParams(search).get('session_id') || '';
}

function hasUnsafeDocId(value) {
  return typeof value !== 'string' || value.trim() === '' || value.includes('/');
}

function PaymentSuccess({ userProfile, onNavigateToHome }) {
  const sessionId = useMemo(() => getSessionId(), []);
  const [status, setStatus] = useState(() => (
    sessionId
      ? {
          type: 'pending',
          title: 'Payment received by Stripe',
          body: 'Waiting for secure token confirmation.'
        }
      : {
          type: 'error',
          title: 'Checkout session missing',
          body: 'We could not find a Stripe session on this return page. Your balance will update only from secure server confirmation.'
        }
  ));

  useEffect(() => {
    if (!sessionId || hasUnsafeDocId(sessionId)) {
      setStatus({
        type: 'error',
        title: 'Checkout session unavailable',
        body: 'This return link is missing a valid Stripe session. Your token balance has not been changed by this page.'
      });
      return undefined;
    }

    if (!userProfile?.uid) {
      setStatus({
        type: 'pending',
        title: 'Payment received by Stripe',
        body: 'Sign-in details are loading. Waiting for secure token confirmation.'
      });
      return undefined;
    }

    const purchaseRef = doc(db, 'tokenPurchases', sessionId);
    const startedAt = Date.now();
    let retryTimerId = null;
    let unsubscribe = null;
    let retryAttempt = 0;
    let hasReceivedSnapshot = false;
    let cancelled = false;

    const clearRetryTimer = () => {
      if (retryTimerId) {
        clearTimeout(retryTimerId);
        retryTimerId = null;
      }
    };

    const showUnavailable = (error) => {
      setStatus({
        type: 'error',
        title: 'Secure confirmation unavailable',
        body: error?.code === 'permission-denied'
          ? 'This purchase confirmation cannot be read by the signed-in PromoGrind account.'
          : 'We could not read the secure purchase confirmation yet. Your balance will update only from server confirmation.'
      });
    };

    const subscribe = () => {
      if (cancelled) {
        return;
      }

      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }

      unsubscribe = onSnapshot(purchaseRef, (snapshot) => {
        hasReceivedSnapshot = true;
        clearRetryTimer();

        if (!snapshot.exists()) {
          setStatus({
            type: 'pending',
            title: 'Payment received by Stripe',
            body: 'Waiting for secure token confirmation. This can take a few moments while Stripe notifies PromoGrind.'
          });
          return;
        }

        const purchase = snapshot.data();
        if (purchase.userId !== userProfile.uid) {
          setStatus({
            type: 'error',
            title: 'Secure confirmation unavailable',
            body: 'This checkout session is not linked to the signed-in PromoGrind account.'
          });
          return;
        }

        if (purchase.status === 'paid_credited') {
          setStatus({
            type: 'confirmed',
            title: 'Tokens confirmed',
            body: `${formatTokens(purchase.tokens)} tokens from ${purchase.packageId || 'your package'} have been securely credited.`,
            purchase
          });
          return;
        }

        setStatus({
          type: 'pending',
          title: 'Payment received by Stripe',
          body: 'Secure token confirmation is still processing.'
        });
      }, (error) => {
        if (cancelled) {
          return;
        }

        const elapsedMs = Date.now() - startedAt;
        const canRetryPermissionDenied =
          error?.code === 'permission-denied'
          && !hasReceivedSnapshot
          && elapsedMs < PURCHASE_CONFIRMATION_RETRY_TIMEOUT_MS;

        if (!canRetryPermissionDenied) {
          showUnavailable(error);
          return;
        }

        setStatus({
          type: 'pending',
          title: 'Payment received by Stripe',
          body: 'Waiting for secure token confirmation. This can take a few moments while Stripe notifies PromoGrind.'
        });

        const baseDelayMs = PURCHASE_CONFIRMATION_RETRY_DELAYS_MS[
          Math.min(retryAttempt, PURCHASE_CONFIRMATION_RETRY_DELAYS_MS.length - 1)
        ];
        const remainingMs = PURCHASE_CONFIRMATION_RETRY_TIMEOUT_MS - elapsedMs;
        const retryDelayMs = Math.min(baseDelayMs, remainingMs);
        retryAttempt += 1;
        clearRetryTimer();
        retryTimerId = setTimeout(subscribe, retryDelayMs);
      });

    };

    subscribe();

    return () => {
      cancelled = true;
      clearRetryTimer();
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [sessionId, userProfile?.uid]);

  const confirmed = status.type === 'confirmed';

  return (
    <div style={pageStyle}>
      <section style={panelStyle}>
        <p style={eyebrowStyle(confirmed)}>Payment return</p>
        <h1 style={headingStyle}>{status.title}</h1>
        <p style={copyStyle}>{status.body}</p>

        {status.type === 'pending' && (
          <p style={noteCopyStyle}>
            The success redirect is not proof of payment. PromoGrind is waiting for
            the trusted server record before showing tokens as credited.
          </p>
        )}

        {confirmed && (
          <div style={confirmationStyle} aria-label="Confirmed token purchase">
            <span style={confirmationLabelStyle}>Confirmed package</span>
            <strong>{formatTokens(status.purchase.tokens)} tokens</strong>
            <span>{status.purchase.packageId}</span>
          </div>
        )}

        {status.type === 'error' && (
          <p style={errorCopyStyle}>
            If payment completed, leave this page and check your balance again shortly.
          </p>
        )}

        <button type="button" onClick={onNavigateToHome} style={primaryButtonStyle}>
          Discovery Dashboard
        </button>
      </section>
    </div>
  );
}

const pageStyle = {
  minHeight: 'calc(100vh - 76px)',
  padding: '48px 16px',
  background: 'linear-gradient(180deg, #f8fafc 0%, #eef6f0 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#1f2937'
};

const panelStyle = {
  width: '100%',
  maxWidth: 620,
  backgroundColor: '#ffffff',
  border: '1px solid #dbe4ee',
  borderRadius: 8,
  padding: 30,
  textAlign: 'center',
  boxShadow: '0 18px 42px rgba(15, 23, 42, 0.1)'
};

const eyebrowStyle = (confirmed) => ({
  margin: '0 0 10px',
  color: confirmed ? '#047857' : '#2563eb',
  fontSize: 13,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0
});

const headingStyle = {
  margin: '0 0 12px',
  color: '#111827',
  fontSize: 32,
  lineHeight: 1.15
};

const copyStyle = {
  margin: '0 auto 18px',
  maxWidth: 500,
  color: '#526070',
  fontSize: 17,
  lineHeight: 1.55
};

const noteCopyStyle = {
  ...copyStyle,
  marginBottom: 24,
  fontSize: 15,
  fontWeight: 800,
  color: '#92400e'
};

const errorCopyStyle = {
  ...copyStyle,
  marginBottom: 24,
  fontSize: 15,
  fontWeight: 800,
  color: '#991b1b'
};

const confirmationStyle = {
  margin: '0 auto 24px',
  padding: 16,
  maxWidth: 360,
  border: '1px solid #86efac',
  borderRadius: 8,
  backgroundColor: '#f0fdf4',
  color: '#065f46',
  display: 'grid',
  gap: 4
};

const confirmationLabelStyle = {
  color: '#047857',
  fontSize: 13,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0
};

const primaryButtonStyle = {
  backgroundColor: '#047857',
  color: '#ffffff',
  border: 'none',
  borderRadius: 8,
  padding: '13px 20px',
  fontSize: 15,
  fontWeight: 800,
  cursor: 'pointer'
};

export default PaymentSuccess;
